import { createFileRoute, Link } from "@tanstack/react-router";

import { PageHero } from "@/components/page-hero";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/support")({
  head: () => ({
    meta: [
      { title: "Support & FAQ — Quibblies" },
      {
        name: "description",
        content:
          "Answers to common Quibblies questions about account approval, review timelines, image moderation and how to appeal a decision.",
      },
      { property: "og:title", content: "Support & FAQ — Quibblies" },
      {
        property: "og:description",
        content:
          "How account approval works on Quibblies, how long review takes and how to appeal a decision.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: SupportPage,
});

const FAQ_GROUPS: { heading: string; items: { q: string; a: React.ReactNode }[] }[] = [
  {
    heading: "Joining and account approval",
    items: [
      {
        q: "How does account approval work?",
        a: (
          <>
            Every Quibblies account goes through the same four steps: sign up, confirm your email,
            application pending, then team review. Because this is an 18+ community, a person reads
            every application before it is approved. You can watch each step move on your{" "}
            <Link className="text-primary-glow underline-offset-4 hover:underline" to="/account/status">
              account status page
            </Link>
            .
          </>
        ),
      },
      {
        q: "How long does review take?",
        a: "Most applications get a decision within 48 hours. Busy weekends can push that out a little; if you have not heard anything after 5 days, contact us and we will chase it.",
      },
      {
        q: "What can I do while I wait?",
        a: "You can browse and read. Publishing, following, saving to your library and commenting unlock once your account is approved.",
      },
      {
        q: "Why was my application not approved?",
        a: "The most common reasons are an unverifiable age check, a username that looks like official Quibblies staff, or a previous account that was removed. The decision notice on your account status page always states the reason given by the team.",
      },
    ],
  },
  {
    heading: "Appeals",
    items: [
      {
        q: "Can I appeal a decision?",
        a: (
          <>
            Yes. You have 30 days to appeal an account or publication decision, and you get one
            appeal per decision. Open your{" "}
            <Link className="text-primary-glow underline-offset-4 hover:underline" to="/account/status">
              account status page
            </Link>{" "}
            and write a short explanation of what you think was misunderstood.
          </>
        ),
      },
      {
        q: "Who reads my appeal?",
        a: "A different team member than the one who made the original decision. Nobody reviews their own call.",
      },
      {
        q: "How long does an appeal take?",
        a: "You get a confirmation the moment you submit. Most appeals are decided within 5 working days, and we always come back to you within 14 days.",
      },
      {
        q: "What happens if my appeal succeeds?",
        a: "Your account or publication is restored to where it was before the decision, and the outcome is recorded in your notices so you have a written trail.",
      },
    ],
  },
  {
    heading: "Profile images and publications",
    items: [
      {
        q: "Why is my new profile image not showing yet?",
        a: "Profile and cover images are reviewed by an Administrator before they go live. Your previous image stays in place until the new one is approved, so your profile never looks broken.",
      },
      {
        q: "My publication is stuck in review — is something wrong?",
        a: "No. Publications are queued for moderation in the order they arrive. Edits to something already published do not take it offline: the live version stays up while the edit is reviewed.",
      },
      {
        q: "Can I write about real personal experiences?",
        a: "Yes — Personal Experience, Reflection and Diary are supported content types. You must acknowledge the privacy guidance and remove details that could identify other people without their consent.",
      },
    ],
  },
  {
    heading: "Safety, privacy and staff",
    items: [
      {
        q: "How do I report something?",
        a: "Use the report action on the publication, profile or comment. Safety reports jump the queue.",
      },
      {
        q: "Can I write under a name that is not my account name?",
        a: "Yes. Pen names decouple your public author identity from your account details, and reading activity is private by default.",
      },
      {
        q: "How do I join the team?",
        a: "Team access is requested from your account and granted only by the Owner. Staff sign in through the separate team entrance and must use two-factor authentication.",
      },
    ],
  },
];

function SupportPage() {
  return (
    <div>
      <PageHero
        eyebrow="Support"
        title="Support & FAQ"
        description="Account approval, review timelines, image moderation and appeals — answered. If your question is not here, the team is one message away."
      />

      <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
        <div className="space-y-10">
          {FAQ_GROUPS.map((group) => (
            <section key={group.heading}>
              <h2 className="mb-3 text-xl font-semibold">{group.heading}</h2>
              <Accordion type="single" collapsible className="rounded-2xl border border-border/70 bg-gradient-card px-4">
                {group.items.map((item) => (
                  <AccordionItem key={item.q} value={item.q}>
                    <AccordionTrigger className="text-left">{item.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{item.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-[1.75rem] border border-border/70 bg-gradient-card p-6 text-center sm:p-8">
          <h2 className="text-lg font-semibold">Still need a person?</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Send us the details and we will reply to your email address — usually within 2 working
            days.
          </p>
          <Button asChild variant="hero" size="pill" className="mt-5">
            <Link to="/contact">Contact the team</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
